import { useState, useEffect, useCallback } from 'react';
import CurrencyManager from '../utils/currencyManager';
import { Currency } from '../utils/currency';
import { debugLog } from '../utils/debug-log';

// Get the singleton instance
const currencyManager = CurrencyManager.getInstance();

// Initialize only once when the first component uses this hook
let initializePromise: Promise<void> | null = null;

export const useCurrencyManager = () => {
  const [currency, setCurrency] = useState<Currency>(currencyManager.getCurrency());
  const [exchangeRate, setExchangeRate] = useState<number | null>(currencyManager.getExchangeRate());
  const [isLoadingRate, setIsLoadingRate] = useState<boolean>(currencyManager.isLoadingRate());
  const [isFallbackRate, setIsFallbackRate] = useState<boolean>(currencyManager.isFallbackRate());
  const [rateTimestamp, setRateTimestamp] = useState<number | null>(currencyManager.getExchangeRateTimestamp());

  // Initialize the manager only once for the entire app
  useEffect(() => {
    if (!initializePromise) {
      debugLog('Initializing currency manager (first time only)...');
      initializePromise = currencyManager.initialize();
    }

    // Subscribe to changes
    const unsubscribe = currencyManager.subscribe(() => {
      setCurrency(currencyManager.getCurrency());
      setExchangeRate(currencyManager.getExchangeRate());
      setIsLoadingRate(currencyManager.isLoadingRate());
      setIsFallbackRate(currencyManager.isFallbackRate());
      setRateTimestamp(currencyManager.getExchangeRateTimestamp());
    });

    return unsubscribe;
  }, []);

  const toggleCurrency = useCallback(() => {
    currencyManager.toggleCurrency();
  }, []);

  const formatAmountSync = useCallback((amount: number, sourceCurrency: Currency = 'USD'): string => {
    return currencyManager.formatAmountSync(amount, sourceCurrency);
  }, []);

  return {
    currency,
    exchangeRate,
    isLoadingRate,
    isFallbackRate,
    rateTimestamp,
    toggleCurrency,
    formatAmountSync,
  };
};
